import React from "react";
import Rule from "../../../Rule";
import Heading from "../../../Heading";
import CaseStudiesCard from "../CaseStudiesCard";
import Stack from "../../../Stack";
import Box from "../../../Box";
import Image from "../../../Image";
import RuralGSTPeriod from "../../../../img/casestudy/storytelling/rural-gst-period.jpg";
import RuralOperationalAdvisory from "../../../../img/casestudy/storytelling/rural-operational-advisory.jpg";
import Logbook from "../../../../img/casestudy/design-sprint/research-4.png";
import BlogLayout from "../BlogLayout";

const Storytelling = (
  <CaseStudiesCard id="storytelling">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      Article
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Visualising customer insights as storyboards to guide team collaboration
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <BlogLayout>
      <Stack spacing="var(--spacing-xx-large)">
        <Box>
          <Heading style={{ maxWidth: "15ch", paddingTop: "var(--spacing-medium)" }}>Storytelling</Heading>
          <p>
            To communicate user insights across teams, I used storyboards and simple visual narratives to bring real
            customer situations to life.
          </p>
          <p>
            <em>
              <strong>Example: </strong>
            </em>
            During the rural accounting initiative, visualising the current user journey helped engineers and designers
            quickly understand context and collaborate more effectively.
          </p>
          <p>
            Over time, this approach became a key part of how I shared discovery findings, turning research insights
            into clear and relatable stories that guided decision-making.
          </p>
        </Box>

        <Stack disableMargin spacing="var(--spacing-xx-large)">
          <Box>
            <Heading mb="small" variant="large">
              Mileage Tracking for Tax
            </Heading>
            <p style={{ maxWidth: "68ch" }}>
              During a discovery interview, a user mentioned needing to track fuel usage and vehicle mileage to separate
              personal and business travel for tax purposes. This was not a feature MYOB’s SME software supported at the
              time, making it a valuable opportunity to explore.
            </p>
            <p style={{ maxWidth: "68ch", marginBottom: "var(--spacing-x-medium)" }}>
              The storyboard visualised how small business owners and accountants manually record trips and expenses in
              physical logbooks, later submitting them for GST and ATO claims. It highlighted the inefficiencies and
              risks of paper-based tracking, showing where digital solutions could simplify record-keeping, ensure
              compliance, and reduce administrative effort.{" "}
            </p>
            <Image dark src={Logbook} alt="Customer Journey Map: Workshop to Final Diagram" />
          </Box>

          <Box margin="0">
            <Heading mb="small" variant="large">
              Rural GST Advisory
            </Heading>
            <p style={{ maxWidth: "68ch", marginBottom: "var(--spacing-x-medium)" }}>
              The storyboard shows an accountant discussing livestock counts with a client at the end of the GST period,
              highlighting how these conversations impact financials and GST reporting.
            </p>

            <Image dark src={RuralGSTPeriod} alt="Rural GST period" caption="Rural GST period" />
            <p
              style={{
                maxWidth: "68ch",
                marginTop: "var(--spacing-x-medium)",
                marginBottom: "var(--spacing-x-medium)",
              }}
            >
              It also captures the wider farm support network of advisors and consultants, revealing opportunities to
              expand MYOB’s rural offering and improve day-to-day operations.
            </p>
            <Image
              dark
              src={RuralOperationalAdvisory}
              alt="Rural operational advisory"
              caption="Rural operational advisory"
            />
            <p style={{ maxWidth: "68ch", marginTop: "var(--spacing-x-medium)" }}>
              At the time, I created these illustrations in Apple Keynote using emojis to bring customer insights to
              life. We printed and displayed them on a shared whiteboard so the whole team could easily reference and
              discuss them.
            </p>
          </Box>
        </Stack>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default Storytelling;
